/**
 * Analytics — tenant-facing Google-Analytics-style reporting over the existing
 * event + session data (FEAT-ANALYTICS-001). All event sources (web, mobile app,
 * API, messaging). Separate feature — does NOT touch Boards.
 *
 * Layout: global xNotify <Header/> + a GA-style grouped left report rail that
 * switches the content pane. Most reports render through the reusable
 * <ExplorerReport> (chart + rich metric table + dimension switcher); Realtime,
 * Overview, Products and Conversions keep their bespoke layouts.
 *
 * Portal (this) → RRS /analytics/* → Event /v2/analytics/* (read-only).
 */
import React, { useMemo, useState, useEffect } from 'react';
import {
  Box, Flex, Heading, Text, VStack, HStack, Icon, Select, Tag, Button, Tooltip,
} from '@chakra-ui/react';
import {
  FiRadio, FiBarChart2, FiUsers, FiUserPlus, FiTrendingUp, FiFileText,
  FiZap, FiGlobe, FiMonitor, FiShoppingBag, FiTarget, FiActivity,
  FiShoppingCart, FiCreditCard, FiLogIn, FiFilter, FiGrid, FiDollarSign, FiPlus, FiRepeat, FiList, FiClock, FiTrendingDown, FiShare2, FiPieChart,
} from 'react-icons/fi';
import Header from '../../components/Header';
import { DateRangeBar } from './components';
import { windowParams, fetchProperties } from './api';
import { ExplorerReport } from './Explorer';
import { JourneyReport } from './JourneyReport';
import { AcquisitionOverviewReport, EngagementOverviewReport, AcquisitionCohortsReport } from './OverviewPages';
import { TransactionsReport } from './TransactionsReport';
import { LifetimeValueReport } from './LifetimeValueReport';
import { EventDetailReport } from './EventDetailReport';
import { ActivityHeatmapReport } from './ActivityHeatmapReport';
import { RetentionCurveReport } from './RetentionCurveReport';
import { PathReport } from './PathReport';
import { RevenueSplitReport } from './RevenueSplitReport';
import { AudiencesReport, useAudiences } from './audience/AudiencesPage';
import { ComparisonBar } from './audience/ComparisonBar';
import { ComparisonControl } from './audience/ComparisonControl';
import { ComparisonDashboard } from './audience/ComparisonDashboard';
import { AudienceBuilder } from './audience/AudienceBuilder';
import {
  OverviewReport, AudienceReport, BehaviorReport,
  ProductsReport, ConversionsReport, RealtimeReport,
} from './reports';

// Explorer-backed report (one config → the full GA report).
const explorer = (cfg) => (params) => <ExplorerReport params={params} {...cfg} />;

// All reports, keyed. `render(params)` returns the content pane.
const REPORTS = {
  realtime: { label: 'Live', icon: FiRadio, hint: 'Active users in the last 30 minutes', render: (p) => <RealtimeReport params={p} /> },
  overview: { label: 'Snapshot', icon: FiBarChart2, hint: 'Users, sessions, pageviews & conversions', render: (p) => <OverviewReport params={p} /> },
  audiences: { label: 'Audiences', icon: FiUsers, hint: 'Each saved audience vs all users — users, sessions & revenue', render: (p, ctx) => <AudiencesReport params={p} onApplyFilter={ctx?.onApplyFilter} /> },

  user_acquisition: {
    label: 'New users by source', icon: FiUserPlus, hint: 'New users by first-touch channel',
    render: explorer({
      dimension: 'first_channel_group', defaultSort: 'users', dimensionLabel: 'First user channel',
      dimensionOptions: [
        { value: 'first_channel_group', label: 'First user channel group' },
        { value: 'first_source', label: 'First user source' },
        { value: 'stream', label: 'Platform (iOS/Android)' },
      ],
      columns: ['users', 'new_users', 'returning_users', 'avg_engagement_seconds', 'event_count', 'key_events', 'revenue'],
    }),
  },
  acquisition_overview: { label: 'Acquisition summary', icon: FiBarChart2, hint: 'New vs returning, channels, cohorts, cities', render: (p) => <AcquisitionOverviewReport params={p} /> },
  acquisition_cohorts: { label: 'Retention cohorts', icon: FiGrid, hint: 'Weekly retention by acquisition cohort', render: (p) => <AcquisitionCohortsReport params={p} /> },
  engagement_overview: { label: 'Engagement summary', icon: FiBarChart2, hint: 'Engaged sessions, stickiness, retention', render: (p) => <EngagementOverviewReport params={p} /> },
  conversion_acquisition: {
    label: 'Converting sources', icon: FiFilter, hint: 'Which source actually converted',
    render: explorer({
      dimension: 'channel_group', defaultSort: 'key_events', dimensionLabel: 'Session channel group',
      dimensionOptions: [
        { value: 'channel_group', label: 'Session channel group' },
        { value: 'first_channel_group', label: 'First user channel group' },
        { value: 'source', label: 'Session source' },
        { value: 'campaign', label: 'Campaign' },
        { value: 'stream', label: 'Platform (iOS/Android)' },
      ],
      columns: ['users', 'sessions', 'key_events', 'conversion_rate', 'revenue'],
      // Key events come from the tenant's declared Conversion Events (RRS injects
      // ?conv=); do NOT bolt on a hardcoded events= list — it over-counted and made
      // this page disagree with Conversions/Snapshot (DA-BUG-0451).
    }),
  },
  traffic_acquisition: {
    label: 'Sessions by source', icon: FiTrendingUp, hint: 'Sessions by channel / source',
    render: explorer({
      dimension: 'channel_group', defaultSort: 'sessions', dimensionLabel: 'Session channel group',
      dimensionOptions: [
        { value: 'channel_group', label: 'Session channel group' },
        { value: 'source', label: 'Session source' },
        { value: 'medium', label: 'Medium' },
        { value: 'campaign', label: 'Campaign' },
        { value: 'stream', label: 'Platform (iOS/Android)' },
      ],
      columns: ['sessions', 'engaged_sessions', 'engagement_rate', 'events_per_session', 'event_count', 'key_events'],
    }),
  },

  pages: {
    label: 'Pages & screens', icon: FiFileText, hint: 'Views & engagement per page/screen',
    render: explorer({
      dimension: 'page', defaultSort: 'views', dimensionLabel: 'Page path and screen',
      columns: ['views', 'users', 'views_per_user', 'avg_engagement_seconds', 'event_count', 'key_events'],
    }),
  },
  events: {
    label: 'Events', icon: FiZap, hint: 'Event volume across all sources',
    render: explorer({
      dimension: 'event', defaultSort: 'event_count', dimensionLabel: 'Event name',
      columns: ['event_count', 'users', 'events_per_user', 'key_events', 'revenue'],
    }),
  },
  event_detail: { label: 'Event detail', icon: FiList, hint: 'Profile a single event — its parameters & value distributions', render: (p) => <EventDetailReport params={p} /> },
  activity: { label: 'Activity times', icon: FiClock, hint: 'When users are active — day \u00d7 hour heatmap (best time to send)', render: (p) => <ActivityHeatmapReport params={p} /> },
  path: { label: 'User flow', icon: FiShare2, hint: 'Step-by-step Sankey of what users do next', render: (p) => <PathReport params={p} /> },
  retention: { label: 'Retention', icon: FiTrendingDown, hint: 'Daily retention curve + Day 1/7/30 for the acquired cohort', render: (p) => <RetentionCurveReport params={p} /> },
  landing_pages: {
    label: 'Entry pages', icon: FiLogIn, hint: 'Entry page/screen that starts each session',
    render: explorer({
      endpoint: 'landing-pages', dimension: 'landing_page', defaultSort: 'sessions', dimensionLabel: 'Landing page',
      columns: ['sessions', 'users', 'new_users', 'avg_engagement_seconds', 'key_events', 'key_event_rate', 'revenue'],
    }),
  },
  behavior: { label: 'Entry & exit', icon: FiActivity, hint: 'Landing & exit pages', render: (p) => <BehaviorReport params={p} /> },

  demographics: {
    label: 'Demographics', icon: FiGlobe, hint: 'Users by geography & language',
    render: explorer({
      dimension: 'country', defaultSort: 'users', dimensionLabel: 'Country',
      dimensionOptions: [
        { value: 'country', label: 'Country' },
        { value: 'city', label: 'City' },
        { value: 'region', label: 'Region' },
        { value: 'language', label: 'Language' },
      ],
      columns: ['users', 'new_users', 'engaged_sessions', 'engagement_rate', 'avg_engagement_seconds', 'event_count'],
    }),
  },
  tech: {
    label: 'Tech', icon: FiMonitor, hint: 'Browser, OS, device & platform',
    render: explorer({
      // Default to OS, not Browser: app-only tenants have no browser data, so a
      // Browser default opened the page on an empty table (DA-BUG-0452).
      dimension: 'os', defaultSort: 'users', dimensionLabel: 'Operating system',
      dimensionOptions: [
        { value: 'os', label: 'Operating system' },
        { value: 'stream', label: 'Platform (iOS/Android)' },
        { value: 'device', label: 'Device' },
        { value: 'channel', label: 'Platform / channel' },
        { value: 'browser', label: 'Browser' },
      ],
      columns: ['users', 'new_users', 'sessions', 'engagement_rate', 'event_count'],
    }),
  },
  audience: { label: 'New vs returning', icon: FiUsers, hint: 'Audience mix, device & geo', render: (p) => <AudienceReport params={p} /> },

  products: { label: 'Products', icon: FiShoppingBag, hint: 'Product views & purchase funnel', render: (p) => <ProductsReport params={p} /> },
  purchase_journey: { label: 'Purchase funnel', icon: FiShoppingCart, hint: 'Session → view → cart → checkout → purchase', render: (p) => <JourneyReport params={p} type="purchase" /> },
  checkout_journey: { label: 'Checkout funnel', icon: FiCreditCard, hint: 'Begin checkout → shipping → payment → purchase', render: (p) => <JourneyReport params={p} type="checkout" /> },
  conversions: { label: 'Conversions', icon: FiTarget, hint: 'Conversion events, rate & value', render: (p) => <ConversionsReport params={p} /> },
  transactions: { label: 'Transactions', icon: FiDollarSign, hint: 'Revenue, orders & recent transactions', render: (p) => <TransactionsReport params={p} /> },
  ltv: { label: 'Lifetime value', icon: FiRepeat, hint: 'Repeat purchase, ARPPU/ARPU & LTV cohorts', render: (p) => <LifetimeValueReport params={p} /> },
  revenue_split: { label: 'New vs returning revenue', icon: FiPieChart, hint: 'Revenue & buyers split by new vs returning users', render: (p) => <RevenueSplitReport params={p} /> },
};

// GA-style grouped rail. `null` title = ungrouped (top).
const GROUPS = [
  { title: null, keys: ['realtime', 'overview'] },
  { title: 'Acquisition', keys: ['acquisition_overview', 'audiences', 'user_acquisition', 'traffic_acquisition', 'conversion_acquisition', 'acquisition_cohorts', 'audience'] },
  { title: 'Engagement', keys: ['engagement_overview', 'retention', 'pages', 'landing_pages', 'events', 'event_detail', 'path', 'activity', 'behavior'] },
  { title: 'People & tech', keys: ['demographics', 'tech'] },
  { title: 'Revenue', keys: ['transactions', 'ltv', 'revenue_split', 'products', 'purchase_journey', 'checkout_journey', 'conversions'] },
];

const PLATFORMS = [
  { value: 'all', label: 'All platforms' },
  { value: 'Web', label: 'Website' },
  { value: 'Mobile', label: 'Mobile app' },
  { value: 'API', label: 'API' },
  { value: 'Message', label: 'Messaging' },
];

// Viewer timezone — '' = the data's native tz (server, Asia/Karachi). The
// browser's own tz is appended at render if it isn't already in the list.
const TIMEZONES = [
  { value: '', label: 'Data default (PKT)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Asia/Karachi', label: 'Pakistan (PKT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Europe/London', label: 'London' },
  { value: 'America/New_York', label: 'New York (ET)' },
];
const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { return ''; } })();

export default function Analytics() {
  const [active, setActive] = useState('overview');
  const [win, setWin] = useState({ mode: 'preset', range: 'last_7_days', from: '', to: '' });
  const [platform, setPlatform] = useState('all'); // platform sub-filter (within a property)
  // GA-style PROPERTY selector (above Live): scopes every report to one property.
  // Properties are derived from observed data; the platform filter refines within one.
  const [property, setProperty] = useState('all');
  const [properties, setProperties] = useState([{ id: 'all', name: 'All properties', type: 'all' }]);
  useEffect(() => {
    let on = true;
    fetchProperties().then((list) => { if (on && list) setProperties(list); });
    return () => { on = false; };
  }, []);
  const [tz, setTz] = useState(''); // viewer timezone ('' = data's native tz, Asia/Karachi)
  const [audienceFilter, setAudienceFilter] = useState(null); // active audience scoping all reports
  const [comparisons, setComparisons] = useState([]); // GA "Add comparison" — up to 4 audiences
  const [builderOpen, setBuilderOpen] = useState(false);
  const comparing = comparisons.length >= 2;
  const { audiences, reload: reloadAudiences } = useAudiences();

  const params = useMemo(
    () => ({
      ...windowParams(win),
      ...(property !== 'all' ? { property } : {}),
      ...(platform !== 'all' ? { channel: platform } : {}),
      ...(tz ? { tz } : {}),
      ...(audienceFilter?.definition ? { audience: JSON.stringify(audienceFilter.definition) } : {}),
    }),
    [win, property, platform, tz, audienceFilter]
  );
  // Comparison mode supplies each audience itself, so the base carries no audience.
  const comparisonParams = useMemo(
    () => ({ ...windowParams(win), ...(property !== 'all' ? { property } : {}), ...(platform !== 'all' ? { channel: platform } : {}), ...(tz ? { tz } : {}) }),
    [win, property, platform, tz]
  );

  const current = REPORTS[active] || REPORTS.overview;
  const platformLabel = PLATFORMS.find((p) => p.value === platform)?.label || 'All platforms';
  const reportCtx = { onApplyFilter: setAudienceFilter };

  return (
    <>
      <Header header="Analytics" />
      <Flex align="stretch" minH="calc(100vh - 70px)" bg="gray.50">
        {/* GA-style grouped left report rail */}
        <Box as="nav" w={{ base: '60px', md: '252px' }} flexShrink={0} bg="white" borderRight="1px solid" borderColor="gray.200" py={3} px={{ base: 2, md: 3 }} overflowY="auto">
          {/* GA-style PROPERTY selector — above Live; scopes every report to one property. */}
          <Box px={1} mb={3} pb={3} borderBottom="1px solid" borderColor="gray.100" display={{ base: 'none', md: 'block' }}>
            <Text px={2} mb={1} fontSize="11px" fontWeight="700" letterSpacing="0.06em" color="gray.400" textTransform="uppercase">Property</Text>
            <Select size="sm" value={property} onChange={(e) => setProperty(e.target.value)} bg="white" title="Select a property (website, mobile app, …)">
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Box>
          {GROUPS.map((g, gi) => (
            <Box key={gi} mb={2}>
              {g.title && (
                <Text px={3} mt={3} mb={1} fontSize="11px" fontWeight="700" letterSpacing="0.06em" color="gray.400" textTransform="uppercase" display={{ base: 'none', md: 'block' }}>
                  {g.title}
                </Text>
              )}
              <VStack align="stretch" spacing="2px">
                {g.keys.map((key) => {
                  const r = REPORTS[key];
                  const isActive = key === active;
                  return (
                    <HStack
                      key={key} as="button" type="button" onClick={() => setActive(key)} title={r.label}
                      spacing={3} px={3} py={2} borderRadius="8px" cursor="pointer" textAlign="left"
                      justify={{ base: 'center', md: 'flex-start' }}
                      bg={isActive ? 'var(--brand-tint)' : 'transparent'}
                      color={isActive ? 'var(--brand-primary)' : 'gray.700'}
                      borderLeft="3px solid" borderColor={isActive ? 'var(--brand-primary)' : 'transparent'}
                      fontWeight={isActive ? '700' : '500'}
                      _hover={{ bg: isActive ? 'var(--brand-tint)' : 'gray.50' }}
                      transition="background .15s, color .15s"
                    >
                      <Icon as={r.icon} boxSize={4} />
                      <Text fontSize="13px" display={{ base: 'none', md: 'block' }}>{r.label}</Text>
                    </HStack>
                  );
                })}
              </VStack>
            </Box>
          ))}
        </Box>

        {/* Content pane */}
        <Box flex="1" minW={0} p={{ base: 3, md: 6 }}>
          <Flex align="center" justify="space-between" wrap="wrap" gap={3} mb={5}>
            <Box>
              <Flex align="center" gap={2}>
                <Heading size="md" color="gray.800">{comparing ? 'Audience comparison' : current.label}</Heading>
                {platform !== 'all' && <Tag size="sm" colorScheme="blue" borderRadius="full">{platformLabel}</Tag>}
                {audienceFilter && (
                  <Tag size="sm" borderRadius="full" variant="subtle" style={{ background: 'var(--brand-tint)', color: 'var(--brand-primary)' }}>
                    {audienceFilter.name}
                    <Box as="button" ml={1} onClick={() => setAudienceFilter(null)} aria-label="clear audience">×</Box>
                  </Tag>
                )}
              </Flex>
              <Text color="gray.500" fontSize="sm" mt={1}>{comparing ? `Comparing ${comparisons.length} audiences on core metrics` : current.hint}</Text>
            </Box>
            <HStack spacing={2} flexWrap="wrap">
              {/* GA "All users / audience" selector — scopes every report. */}
              <ComparisonBar audiences={audiences} filter={audienceFilter} onFilter={setAudienceFilter} onNew={() => setBuilderOpen(true)} />
              {/* GA "Add comparison +" — compare up to 4 audiences side by side. */}
              <ComparisonControl audiences={audiences} comparisons={comparisons} onChange={setComparisons} />
              {/* Explicit GA-style "+" to build a new audience from any report. */}
              <Tooltip label="Create audience" hasArrow>
                <Button size="sm" variant="outline" bg="white" leftIcon={<FiPlus />} onClick={() => setBuilderOpen(true)}>
                  Audience
                </Button>
              </Tooltip>
              {/* Data-stream / property scope — which source am I viewing? */}
              <Select size="sm" w="auto" value={platform} onChange={(e) => setPlatform(e.target.value)} bg="white">
                {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </Select>
              {/* Viewer timezone — re-localizes day/hour buckets + date windows. */}
              <Tooltip label="Report timezone" hasArrow>
                <Select size="sm" w="auto" value={tz} onChange={(e) => setTz(e.target.value)} bg="white">
                  {TIMEZONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  {browserTz && !TIMEZONES.some((t) => t.value === browserTz) && (
                    <option value={browserTz}>{`Your time (${browserTz})`}</option>
                  )}
                </Select>
              </Tooltip>
              <DateRangeBar value={win} onChange={setWin} />
            </HStack>
          </Flex>
          {/* Comparison mode overlays up to 4 audiences; otherwise the active report.
              key forces a fresh mount per report + when the audience filter changes. */}
          {comparing ? (
            <ComparisonDashboard comparisons={comparisons} params={comparisonParams} />
          ) : (
            <Box key={`${active}:${audienceFilter?.id || 'all'}`}>{current.render(params, reportCtx)}</Box>
          )}
        </Box>
      </Flex>

      <AudienceBuilder isOpen={builderOpen} onClose={() => setBuilderOpen(false)} onSaved={reloadAudiences} />
    </>
  );
}
